// Cloud Save Service - Netflix/Duolingo-style cloud sync
// No credit card required, free for up to 50,000 users
// Now enhanced with Real-Time Sync support

import { createClient } from '@supabase/supabase-js';
import { userAuth } from './userAuth';
import { logger } from '../utils/logger';
import { realTimeSync } from './realTimeSync';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

// Feature flag: Enable real-time sync (can be toggled)
const ENABLE_REAL_TIME_SYNC = true;

export interface UserProgress {
  userId: string;
  testHistory: any[];
  studyTime: number;
  averageScore: number;
  lastSync: string;
}

class CloudSaveService {
  private isOnline: boolean = navigator.onLine;

  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.syncToCloud();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  // Save user progress locally and attempt cloud sync (automatic)
  async saveProgress(userId: string, progressData: any): Promise<boolean> {
    try {
      // Use real-time sync if enabled (Netflix/Duolingo-style)
      if (ENABLE_REAL_TIME_SYNC) {
        return await realTimeSync.saveProgress(userId, progressData);
      }

      // Fallback to legacy sync
      // Always save locally first
      const progress: UserProgress = {
        userId,
        testHistory: progressData.testHistory || [],
        studyTime: progressData.studyTime || 0,
        averageScore: progressData.averageScore || 0,
        lastSync: new Date().toISOString()
      };

      // Save to localStorage
      localStorage.setItem(`userProgress_${userId}`, JSON.stringify(progress));
      
      // Automatically sync to cloud if online (no user action needed)
      if (this.isOnline) {
        await this.syncToCloud();
      }

      logger.debug('Progress saved and synced for user:', userId);
      return true;
    } catch (error) {
      logger.error('Error saving progress:', error);
      return false;
    }
  }

  // Load user progress from local storage
  async loadProgress(userId: string): Promise<UserProgress | null> {
    try {
      // Use real-time sync if enabled (Netflix/Duolingo-style)
      if (ENABLE_REAL_TIME_SYNC) {
        return await realTimeSync.loadProgress(userId);
      }

      // Fallback to legacy load
      const saved = localStorage.getItem(`userProgress_${userId}`);
      if (saved) {
        const progress = JSON.parse(saved);
        logger.debug('Progress loaded for user:', userId);
        return progress;
      }
      return null;
    } catch (error) {
      logger.error('Error loading progress:', error);
      return null;
    }
  }

  // Sync to cloud using Supabase (automatic background sync)
  private async syncToCloud(): Promise<void> {
    try {
      const currentUser = userAuth.getCurrentUser();
      if (!currentUser || currentUser.isGuest) {
        // Don't sync guest users
        return;
      }

      const allProgress = this.getAllProgress(currentUser.id);
      
      // Load exam date and weak areas from localStorage
      const examDateStr = localStorage.getItem('userExamDate');
      const examDate = examDateStr ? new Date(examDateStr).toISOString().split('T')[0] : null; // Convert to DATE format (YYYY-MM-DD)
      
      const weakAreasStr = localStorage.getItem('deferredWeakAreas');
      const weakAreas = weakAreasStr ? JSON.parse(weakAreasStr) : [];

      const progressData = {
        user_id: currentUser.id,
        test_history: JSON.stringify(allProgress?.testHistory || []),
        study_time: allProgress?.studyTime || 0,
        average_score: allProgress?.averageScore || 0,
        total_tests: allProgress?.testHistory?.length || 0,
        mock_exam_results: JSON.stringify(allProgress?.mockExamResults || []),
        exam_date: examDate,
        weak_areas: JSON.stringify(weakAreas),
        last_sync: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Upsert (insert or update) user progress automatically
      const { error } = await supabase
        .from('user_progress')
        .upsert(progressData, { onConflict: 'user_id' });

      if (error) {
        logger.error('Supabase sync error:', error);
      } else {
        logger.debug('Progress automatically synced to Supabase');
      }
    } catch (error) {
      logger.error('Cloud sync error:', error);
    }
  }

  // Check if user has saved progress
  hasSavedProgress(userId: string): boolean {
    return localStorage.getItem(`userProgress_${userId}`) !== null;
  }

  // Get all saved progress for a user
  getAllProgress(userId: string): any {
    const saved = localStorage.getItem(`userProgress_${userId}`);
    return saved ? JSON.parse(saved) : null;
  }

  // Clear all progress (for testing)
  clearProgress(userId: string): void {
    localStorage.removeItem(`userProgress_${userId}`);
    logger.debug('Progress cleared for user:', userId);
  }
}

export const cloudSave = new CloudSaveService();
